export type SupportChatMessage = {
  id: string
  sender: "customer" | "admin" | "system" | string
  body: string
  author_name?: string | null
  created_at: string
}

export type SupportChatProfile = {
  name: string
  email: string
}

function publishableKey(): string {
  return process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY || ""
}

function headers(): HeadersInit {
  const key = publishableKey()
  return {
    "Content-Type": "application/json",
    ...(key ? { "x-publishable-api-key": key } : {}),
  }
}

export async function fetchSupportChatCaptcha(): Promise<{
  question: string
  token: string
}> {
  const res = await fetch("/store/support-chat/captcha", {
    method: "GET",
    headers: headers(),
    cache: "no-store",
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || "captcha_failed")
  }
  return data
}

export async function startSupportChatSession(input: {
  visitor_key: string
  name: string
  email: string
  captcha_token: string
  captcha_answer: string
}): Promise<{
  conversation_id: string
  name: string
  email: string
  status: string
}> {
  const res = await fetch("/store/support-chat/session", {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(input),
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || "start_failed")
  }
  return data
}

export async function endSupportChatSession(input: {
  visitor_key: string
  conversation_id: string
}): Promise<{
  conversation_id: string
  status: string
  email_sent: boolean
  emailed_to?: string
  emailed_cc?: string | null
}> {
  const res = await fetch("/store/support-chat/end", {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(input),
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || "end_failed")
  }
  return data
}

export async function sendSupportChatMessage(input: {
  visitor_key: string
  conversation_id?: string | null
  body: string
  name?: string
  contact?: string
}): Promise<{
  conversation_id: string
  message: SupportChatMessage
  discord_ok: boolean
}> {
  const res = await fetch("/store/support-chat/messages", {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(input),
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || "send_failed")
  }
  return data
}

export async function listSupportChatMessages(input: {
  visitor_key: string
  conversation_id: string
  since?: string | null
}): Promise<{
  conversation_id: string
  status: string
  messages: SupportChatMessage[]
}> {
  const params = new URLSearchParams({
    visitor_key: input.visitor_key,
    conversation_id: input.conversation_id,
  })
  if (input.since) {
    params.set("since", input.since)
  }
  const res = await fetch(`/store/support-chat/messages?${params}`, {
    method: "GET",
    headers: headers(),
    cache: "no-store",
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || "list_failed")
  }
  return data
}

export function getOrCreateVisitorKey(): string {
  if (typeof window === "undefined") {
    return ""
  }
  const keyName = "vx-support-visitor-v1"
  try {
    const existing = localStorage.getItem(keyName)
    if (existing && existing.length >= 8) {
      return existing
    }
    const created =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`
    localStorage.setItem(keyName, created)
    return created
  } catch {
    return `v_${Date.now()}`
  }
}
